import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Sparkles, Cpu, RefreshCw } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo85SistemaAutonomo() {
    const [accion, setAccion] = useState('');
    const [entorno, setEntorno] = useState('');
    const [disparador, setDisparador] = useState('');
    const [medicion, setMedicion] = useState('');
    const [ajuste, setAjuste] = useState('');
    const [proteccion, setProteccion] = useState('');
    const [refuerzo, setRefuerzo] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [loadingPreload, setLoadingPreload] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [nivelAutonomia, setNivelAutonomia] = useState('');

    useEffect(() => {
        const init = async () => {
            try {
                const [draftRes, preloadRes] = await Promise.all([
                    axios.get(`${API_URL}/api/user/draft/sistema_autonomo`, { withCredentials: true }).catch(() => ({ data: {} })),
                    axios.get(`${API_URL}/api/user/modules/sistema-autonomo/preload`, { withCredentials: true }).catch(() => ({ data: {} }))
                ]);
                const draft = draftRes.data?.data || {};
                const preload = preloadRes.data || {};
                setAccion(draft.accion || preload.accion || '');
                setEntorno(draft.entorno || preload.entorno || '');
                setDisparador(draft.disparador || preload.disparador || '');
                setMedicion(draft.medicion || preload.medicion || '');
                setAjuste(draft.ajuste || preload.ajuste || '');
                setProteccion(draft.proteccion || preload.proteccion || '');
                setRefuerzo(draft.refuerzo || preload.refuerzo || '');
                if (draftRes.data.read_only) { setReadOnly(true); setSubmittedAt(draftRes.data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); setLoadingPreload(false); }
        };
        init();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'sistema_autonomo', data: { accion, entorno, disparador, medicion, ajuste, proteccion, refuerzo } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [accion, entorno, disparador, medicion, ajuste, proteccion, refuerzo]);

    useEffect(() => { if (loadingDraft || loadingPreload || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [accion, entorno, disparador, medicion, ajuste, proteccion, refuerzo, saveDraft, loadingDraft, loadingPreload, readOnly]);

    const canSubmit = !readOnly && (accion.trim().length > 0 && entorno.trim().length > 0 && disparador.trim().length > 0 && medicion.trim().length > 0 && ajuste.trim().length > 0 && proteccion.trim().length > 0 && refuerzo.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/sistema-autonomo/submit`, {
                accion, entorno, disparador, medicion, ajuste, proteccion, refuerzo
            }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setNivelAutonomia(data.nivel_autonomia);
            setSubmitted(true);
            toast.success('¡Capítulo 8 completado!');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft || loadingPreload) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#5FAF9D] to-[#3E5F6E] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><Cpu className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="sistema-autonomo-success-title">¡Sistema autónomo completo!</h2>
                    <p className="text-[#7A9BA8] font-medium mb-4">Has integrado todos los componentes en un sistema que funciona solo</p>
                    {nivelAutonomia && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Autonomía: {nivelAutonomia}</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="sistema_autonomo" chapter={8} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-sistema-autonomo">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    const fields = [
        { key: 'accion', label: 'Acción principal', hint: '¿Cuál es la acción central de tu sistema?', value: accion, setter: setAccion, testId: 'autonomo-accion' },
        { key: 'entorno', label: 'Entorno', hint: '¿Cómo está configurado tu espacio para facilitar la acción?', value: entorno, setter: setEntorno, testId: 'autonomo-entorno' },
        { key: 'disparador', label: 'Disparador', hint: '¿Qué activa tu acción automáticamente?', value: disparador, setter: setDisparador, testId: 'autonomo-disparador' },
        { key: 'medicion', label: 'Medición', hint: '¿Cómo mides si estás ejecutando?', value: medicion, setter: setMedicion, testId: 'autonomo-medicion' },
        { key: 'ajuste', label: 'Ajuste', hint: '¿Cómo ajustas cuando la intensidad baja?', value: ajuste, setter: setAjuste, testId: 'autonomo-ajuste' },
        { key: 'proteccion', label: 'Protección', hint: '¿Cuál es tu respuesta ante señales de recaída?', value: proteccion, setter: setProteccion, testId: 'autonomo-proteccion' },
        { key: 'refuerzo', label: 'Refuerzo', hint: '¿Cómo te recompensas después de ejecutar?', value: refuerzo, setter: setRefuerzo, testId: 'autonomo-refuerzo' },
    ];

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#EAE8E3]/50 sticky top-0 z-50">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/usuario" className="flex items-center gap-2 text-[#3E5F6E] hover:text-[#2F4A57]"><ArrowLeft className="h-5 w-5" /><span className="text-sm font-medium">Volver</span></Link>
                    <span className="text-xs text-[#9AA5B1]">{savingDraft ? 'Guardando...' : 'Borrador guardado'}</span>
                </div>
            </header>
            <main className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2"><div className="w-10 h-10 bg-gradient-to-br from-[#5FAF9D] to-[#3E5F6E] rounded-xl flex items-center justify-center"><Cpu className="h-5 w-5 text-white" /></div><span className="text-xs font-semibold text-[#9AA5B1] uppercase tracking-wider">Capítulo 8 · Módulo 8.5</span></div>
                    <h1 className="text-2xl sm:text-3xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Sistema autónomo</h1>
                    <p className="text-[#616E7C]">Integra acción, disparador, entorno, medición, ajuste, protección y refuerzo en un sistema autosuficiente.</p>
                    <div className="mt-3 flex items-center gap-2 text-xs text-[#5FAF9D] bg-[#5FAF9D]/5 px-3 py-2 rounded-lg">
                        <RefreshCw className="h-3.5 w-3.5" />
                        <span>Los campos se han precargado con datos de tus módulos anteriores. Puedes editarlos.</span>
                    </div>
                </div>
                <div className="space-y-5">
                    {fields.map(f => (
                        <div key={f.key}>
                            <Label className="text-sm font-semibold text-[#1F2A30] mb-1.5 block">{f.label}</Label>
                            <p className="text-xs text-[#9AA5B1] mb-2">{f.hint}</p>
                            <Textarea value={f.value} onChange={e => f.setter(e.target.value)} placeholder={f.hint} className="min-h-[70px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid={f.testId} />
                        </div>
                    ))}
                    <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="w-full bg-gradient-to-r from-[#5FAF9D] to-[#3E5F6E] hover:opacity-90 text-white py-3 rounded-xl" data-testid="submit-sistema-autonomo">
                        {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analizando sistema...</> : 'Completar sistema autónomo'}
                    </Button>
                </div>
            </main>
            <SENDAFooter />
        </div>
    );
}
